import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { ChevronUp } from "lucide-react";
import { effectiveScrollY } from "@/lib/body-scroll-lock";

const SCROLL_THRESHOLD = 400;

export function BackToTop() {
  const [visible, setVisible] = useState(false);
  const [location] = useLocation();

  // Slide decks own their whole viewport and have their own bottom nav. This
  // FAB was landing on top of it, and on mobile it sat over body text.
  const onDeck = location.startsWith("/training/");

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setVisible(effectiveScrollY() > SCROLL_THRESHOLD);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (onDeck) return null;

  // 2026-09-09: dropped `backdrop-blur-sm` from the button below — a fixed
  // 40x40 element at 80% charcoal, so the blur behind it was never visible,
  // but it forced a backdrop-filter pass on every scroll frame for the life
  // of the page.
  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 z-50 w-10 h-10 rounded-[4px] border border-cream/15 bg-charcoal/80 flex items-center justify-center cursor-pointer hover:border-cream/30 transition-all duration-250 group"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(12px)",
        pointerEvents: visible ? "auto" : "none",
      }}
      aria-label="Back to top"
      data-testid="button-back-to-top"
    >
      <ChevronUp className="w-4 h-4 text-cream/70 group-hover:text-cream/90 transition-colors duration-200" />
    </button>
  );
}
